import { Button, Grid } from '@mui/material';
import { Box } from '@mui/system';
import CommonDialog from 'components/common/CommonDialog';
import CardBodyComponent from 'components/common/card/CardBodyComponent';
import CardTooltipButton from 'components/common/card/CardTooltipButton';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setInBulkClosing } from 'reducers/serviceAgreementsReducer';
import { AppDispatch, RootState } from 'store';
import { jbColors } from 'theme/jbColors';
import { fetchContactNotesByBrUid } from 'thunks/accountServiceAgreementsThunk';
import { cardStyle } from 'views/OrderNewProductOrService/OrderNewProductOrService';
import CloseIcon from 'assets/icons/close-icon.svg?react';
import AddDocument from 'assets/icons/add-document-icon.svg?react';
import CustomStepMetadata from 'components/customStepMetadata/CustomStepMetadata';
import { Features } from 'reducers/partnerStatusReducer';
import { FeatureEligibility } from 'pco-product-service-internal-consumer';

type CloseAllProductsProps = {
  partnerId?: string;
  agreementsToClose: string[];
};

const CloseAllProducts = ({ partnerId, agreementsToClose }: CloseAllProductsProps) => {
  const [open, setOpen] = React.useState(false);
  const [attestationOpen, setAttestationOpen] = React.useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const brUid = useSelector((state: RootState) => state.account.brUid);
  const contactNote = useSelector((state: RootState) => state.serviceAgreements.contactNote);
  const inBulkClosing = useSelector((state: RootState) => state.serviceAgreements.inBulkClosing);
  const [prevInBulkClosing, setPrevInBulkClosing] = React.useState(inBulkClosing);
  if (inBulkClosing !== prevInBulkClosing) {
    setPrevInBulkClosing(inBulkClosing);
    setOpen(false);
  }

  const isEligibleForBulkClosure =
    useSelector(
      (state: RootState) =>
        state.partnerStatus.eligibility.find(
          (eligibilityType: FeatureEligibility) => eligibilityType.feature === Features.BULK_CLOSURE,
        )?.isEligible,
    ) ?? false;

  useEffect(() => {
    if (brUid) {
      dispatch(fetchContactNotesByBrUid(brUid));
    }
    if (partnerId) {
      dispatch(fetchContactNotesByBrUid(partnerId));
    }
  }, [dispatch, brUid, partnerId]);

  return (
    <Grid data-testid="close-all-products" item sx={cardStyle}>
      <CardBodyComponent
        content={`Close all active and in-opening products or services for this ${partnerId ? 'partner' : 'business relation.'}`}
      />
      <CardTooltipButton
        id={'close-all-products-button'}
        data-testid={'close-all-products-button'}
        buttonText={'Close all'}
        isEligible={agreementsToClose.length > 0 && isEligibleForBulkClosure}
        handleClick={() => {
          setOpen(true);
        }}
        tooltipTitle={
          agreementsToClose.length > 0 ? 'Close all products and services.' : 'No products / services to close.'
        }
      />
      <CommonDialog
        title={'Close all'}
        removeCancel={true}
        content={
          <div>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontWeight: 'bold' }}>
                <span style={{ textDecoration: 'underline' }}>Important: </span>
                This functionality should only be used in the context of the intermediary termination
              </p>
              <p>
                {' '}
                This feature allows for triggering bulk closure of products and services in CLM. However, note that for
                products requiring additional steps to complete the termination or technical deactivation,
                <span style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                  those steps must still be performed.
                </span>
                {`These products can be identified as they will still appear in the "active" tab and have the status "in closing".`}
              </p>
              <p>{`Products that don't require any additional step will be set as "inactive" directly.`}</p>
              <p>
                Please confirm if you would like to proceed with closing all active and in-opening products and services
                associated with this {partnerId ? 'partner' : 'business relation'}.
              </p>
              <p style={{ fontWeight: 'bold' }}>
                <span style={{ textDecoration: 'underline' }}>Note: </span>
                All related roles will be closed and you will not be able to reactivate the product or service.
              </p>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button
                onClick={() => {
                  setOpen(false);
                }}
                variant="contained"
                sx={{
                  color: jbColors.core.reflexBlue[100],
                  backgroundColor: 'white',
                  border: `1px solid ${jbColors.core.reflexBlue[100]}`,
                  '&:hover': {
                    backgroundColor: jbColors.core.reflexBlue[100],
                    color: 'white',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  dispatch(setInBulkClosing(true));
                  setAttestationOpen(true);
                }}
                variant="contained"
                color="primary"
              >
                Confirm to proceed
              </Button>
            </Box>
          </div>
        }
        isOpen={open}
        onClose={() => {
          setOpen(false);
        }}
      />
      <CommonDialog
        isOpen={attestationOpen}
        sx={{ maxWidth: '60em' }}
        onClose={() => setAttestationOpen(false)}
        title={'Attestation'}
        titleIcon={<AddDocument />}
        closeIcon={
          <CloseIcon style={{ fill: jbColors.core.reflexBlue[100] }} onClick={() => setAttestationOpen(false)} />
        }
        removeCancel={true}
        content={
          <CustomStepMetadata
            isReadOnly={false}
            customStepMetadata={{
              customComponent: 'AttestationConfirmation',
              componentProps: JSON.stringify({
                contactList: contactNote?.contactList ?? [],
              }),
              workAccess: true,
              isPmkTask: false,
            }}
            closeAction={() => setAttestationOpen(false)}
          />
        }
      />
    </Grid>
  );
};

export default CloseAllProducts;
